import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { LocalStorageService } from '../localStorage/local-storage.service';
import {
  CreateMenuRequest,
  UpdateMenuRequest,
} from '../../../core/interfaces/admin-menu';
import {
  CreateUsuarioRequest,
  FiltroUsuarios,
  UpdateUsuarioRequest,
} from '../../../core/interfaces/usuario';

@Injectable({
  providedIn: 'root',
})
export class HttpClientService {
  //private readonly baseApiUrl = `${environment.baseUrl}/apis/api-adesao-automatica-previdencia/v1`;
  private readonly baseApiUrl = `${environment.baseUrl}${environment.apiPrefix}/v1`;
  private httpClient: HttpClient;

  constructor(
    httpClient: HttpClient,
    private localStorage: LocalStorageService,
  ) {
    this.httpClient = httpClient;
  }

  async listAdminMenus(): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/menus`;
    return this.executeRequest('GET', url);
  }

  /**
   * Catálogo administrativo em árvore: alimenta o combo de grupo pai da tela de
   * configuração. Sem filtro nem poda — inclui item inativo e grupo vazio.
   */
  async getAdminMenuTree(): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/menus/tree`;
    return this.executeRequest('GET', url);
  }

  async getAdminMenuById(menuId: number): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/menus/${menuId}`;
    return this.executeRequest('GET', url);
  }

  async createMenu(menu: CreateMenuRequest): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/menus`;
    return this.executeRequest('POST', url, { body: menu });
  }

  async updateMenu(
    menuId: number,
    menu: UpdateMenuRequest,
  ): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/menus/${menuId}`;
    return this.executeRequest('PUT', url, { body: menu });
  }

  async deleteMenu(menuId: number): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/menus/${menuId}`;
    return this.executeRequest('DELETE', url);
  }

  async cloneMenu(menuId: number): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/menus/${menuId}/clone`;
    return this.executeRequest('POST', url);
  }

  async listUsuarios(filtro: FiltroUsuarios): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/usuarios`;
    const params = this.buildQueryParams({
      page: filtro.page,
      pageSize: filtro.pageSize,
      search: filtro.search,
      ativoVivestCenter: filtro.ativoVivestCenter,
    });
    return this.executeRequest('GET', url, { params });
  }

  async createUsuario(usuario: CreateUsuarioRequest): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/usuarios`;
    return this.executeRequest('POST', url, { body: usuario });
  }

  async getUsuarioById(usuarioId: number): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/usuarios/${usuarioId}`;
    return this.executeRequest('GET', url);
  }

  async updateUsuario(
    usuarioId: number,
    payload: UpdateUsuarioRequest,
  ): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/usuarios/${usuarioId}`;
    return this.executeRequest('PUT', url, { body: payload });
  }

  async getGruposLdapUsuario(usuarioId: number): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/admin/usuarios/${usuarioId}/grupos-ldap`;
    return this.executeRequest('GET', url);
  }

  async refreshToken(refreshToken: string): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/auth/refresh`;
    return this.executeRequest('POST', url, { body: { token: { refresh: refreshToken } } });
  }

  async login(cpf: string, password: string): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/auth/login`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': environment.ocpapimsubscriptionkey,
    });

    const credentials = {
      user: { cpf, password },
    };

    return this.executeRequest('POST', url, {
      body: credentials,
      customHeaders: headers,
    });
  }

  async loginIntegWeb(login: string, password: string): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/auth/integweb-login`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': environment.ocpapimsubscriptionkey,
    });

    const credentials = {
      user: { login, password },
    };

    return this.executeRequest('POST', url, {
      body: credentials,
      customHeaders: headers,
    });
  }

  async logondAd(
    tokenAd: string,
    endpoint: string = 'ad-login',
  ): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/auth/${endpoint}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${tokenAd}`,
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': `${environment.ocpapimsubscriptionkey}`,
    });
    return this.executeRequest('POST', url, {
      customHeaders: headers,
    });
  }

  async getMe(): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/auth/me`;
    return this.executeRequest('GET', url);
  }

  async logout(refreshToken: string): Promise<HttpResponse<any>> {
    const url = `${this.baseApiUrl}/auth/logout`;
    return this.executeRequest('POST', url, { body: { refreshToken } });
  }

  private buildQueryParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(
          key,
          typeof value === 'boolean' ? value.toString() : value,
        );
      }
    });

    return httpParams;
  }

  private getDefaultHeaders(): HttpHeaders {
    const token = String(this.localStorage.get('bearerToken')).trim();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': `${environment.ocpapimsubscriptionkey}`,
    });
    return headers;
  }

  private formatarDataDDMMYYYY(data: string): string {
    if (!data) return '';

    // Para inputs do tipo date, o formato é yyyy-mm-dd
    if (data.includes('-')) {
      const partes = data.split('-');
      if (partes.length === 3) {
        const [ano, mes, dia] = partes;
        // Garantir que dia e mes tenham 2 dígitos
        const diaFormatado = dia.padStart(2, '0');
        const mesFormatado = mes.padStart(2, '0');
        return `${diaFormatado}${mesFormatado}${ano}`;
      }
    }

    // Se já estiver no formato ddmmyyyy, retornar como está
    const dataLimpa = data.replace(/[^0-9]/g, '');
    if (dataLimpa.length === 8) {
      return dataLimpa;
    }

    // Para outros formatos, retornar a data limpa
    return dataLimpa;
  }

  private async executeRequest(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    options: {
      body?: any;
      params?: HttpParams;
      customHeaders?: HttpHeaders;
    } = {},
  ): Promise<HttpResponse<any>> {
    const headers = options.customHeaders || this.getDefaultHeaders();
    const requestOptions = {
      headers,
      params: options.params,
      observe: 'response' as const,
    };

    switch (method) {
      case 'GET':
        return lastValueFrom(this.httpClient.get(url, requestOptions));
      case 'POST':
        return lastValueFrom(
          this.httpClient.post(url, options.body, requestOptions),
        );
      case 'PUT':
        return lastValueFrom(
          this.httpClient.put(url, options.body, requestOptions),
        );
      case 'DELETE':
        return lastValueFrom(this.httpClient.delete(url, requestOptions));
      case 'PATCH':
        return lastValueFrom(
          this.httpClient.patch(url, options.body, requestOptions)
        );
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }
}
